import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import dayGridPlugin from "@fullcalendar/daygrid";
import multiMonthPlugin from "@fullcalendar/multimonth";
import FullCalendar from "@fullcalendar/react";

import {
  formatDate,
  combineDateAndTime,
  formatTime,
} from "../../utils/helpers";
import CourseInfoDialog from "./dialogs/CourseInfoDialog ";
import { getCursoById, updateCurso } from "../../api/curso";
import { getEventoById } from "../../api/evento";

export default function FullCalendarView({ cursos, onCursoUpdated }) {
  const user = useSelector((state) => state.authentication.user);
  const [eventos, setEventos] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [curso, setCurso] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const calendarRef = useRef(null);

  // Generar opciones de años (5 años atrás hasta 5 años adelante)
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      years.push(i);
    }
    return years;
  };

  // Manejar cambio de año
  const handleYearChange = (event) => {
    const newYear = parseInt(event.target.value);
    setSelectedYear(newYear);

    // Obtener el calendario y navegar al nuevo año
    const calendarApi = calendarRef.current.getApi();
    const currentDate = calendarApi.getDate();
    const newDate = new Date(newYear, currentDate.getMonth(), 1);
    calendarApi.gotoDate(newDate);
  };

  const handleClickChange = async (info) => {
    const eventId = info.event.id;
    
    // Identificar si es evento de inicio o fin y extraer el ID del curso
    const esInicio = eventId.endsWith("-inicio");
    const esFin = eventId.endsWith("-fin");
    const cursoId = esInicio || esFin 
      ? eventId.replace(/-inicio$|-fin$/, "")
      : eventId;
    
    const curso = cursos.find((curso) => curso.id === cursoId);
    if (!curso) {
      console.error("Curso not found with ID:", cursoId);
      return;
    }
    try {
      const resCurso = await getCursoById(cursoId);
      setCurso(resCurso.data);
      setDialogOpen(true);
    } catch (error) {
      try {
        const resEvent = await getEventoById(cursoId);
        setCurso(resEvent.data);
        setDialogOpen(true);
      } catch (error) {
        console.error(error);
      }
    }
  };

  // Función para actualizar las fechas del curso en la BD
  const updateCursoFechas = async (cursoId, nuevaFechaIni, nuevaFechaFin) => {
    try {
      setLoading(true);

      // Obtener los datos actuales del curso
      const cursoResponse = await getCursoById(cursoId);
      const cursoActual = cursoResponse.data;

      // Crear el objeto con los datos actualizados
      const cursoActualizado = {
        ...cursoActual,
        fechaIni: nuevaFechaIni,
        fechaFin: nuevaFechaFin,
      };

      // Actualizar en la base de datos
      await updateCurso(cursoId, cursoActualizado);

      console.log("Curso actualizado exitosamente:", cursoActualizado);

      // Llamar callback para actualizar la lista de cursos en el componente padre
      if (onCursoUpdated) {
        onCursoUpdated(cursoActualizado);
      }

      return true;
    } catch (error) {
      console.error("Error al actualizar el curso:", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Manejador cuando se arrastra un evento (cambia la fecha de inicio o fin)
  const handleEventDrop = async (info) => {
    const eventId = info.event.id;
    const nuevaFecha = info.event.start.toISOString();

    // Identificar si es evento de inicio o fin
    const esInicio = eventId.endsWith("-inicio");
    const esFin = eventId.endsWith("-fin");
    
    // Extraer el ID del curso (eliminar el sufijo -inicio o -fin)
    const cursoId = esInicio || esFin 
      ? eventId.replace(/-inicio$|-fin$/, "")
      : eventId;

    // Obtener el curso actual para mantener la otra fecha
    try {
      const cursoResponse = await getCursoById(cursoId);
      const cursoActual = cursoResponse.data;
      
      const nuevaFechaIni = esInicio ? nuevaFecha : cursoActual.fechaIni;
      const nuevaFechaFin = esFin ? nuevaFecha : cursoActual.fechaFin;

      console.log("Evento arrastrado:", {
        id: cursoId,
        tipo: esInicio ? "inicio" : esFin ? "fin" : "completo",
        nuevaFechaIni,
        nuevaFechaFin,
      });

      const success = await updateCursoFechas(
        cursoId,
        nuevaFechaIni,
        nuevaFechaFin
      );

      if (!success) {
        // Si falla la actualización, revertir el cambio visual
        info.revert();
        alert("Error al actualizar las fechas del curso. Inténtalo de nuevo.");
      }
    } catch (error) {
      console.error("Error al obtener el curso:", error);
      info.revert();
      alert("Error al actualizar las fechas del curso. Inténtalo de nuevo.");
    }
  };

  // Manejador cuando se redimensiona un evento (no aplicable para eventos de inicio/fin separados)
  const handleEventResize = async (info) => {
    // Para eventos de inicio/fin separados, no permitimos redimensionar
    // Solo revertir el cambio
    info.revert();
  };

  // Manejador para crear nuevo evento al seleccionar un rango (opcional)
  const handleDateSelect = (info) => {
    console.log("Rango seleccionado:", {
      start: info.start,
      end: info.end,
    });

    // Aquí podrías implementar la lógica para crear un nuevo curso
    // Por ejemplo, abrir un modal para crear un nuevo curso

    // Limpiar la selección
    info.view.calendar.unselect();
  };

  // Manejar cuando el usuario navega manualmente por el calendario
  const handleDatesSet = (info) => {
    const currentDate = info.view.currentStart;
    const year = currentDate.getFullYear();
    if (year !== selectedYear) {
      setSelectedYear(year);
    }
  };

  useEffect(() => {
    const eventos = [];
    
    cursos.forEach((curso) => {
      const titulo = curso.titulo || "Sin título";
      const fechaIni = new Date(curso.fechaIni);
      const fechaFin = curso.fechaFin ? new Date(curso.fechaFin) : null;
      
      // Validar fechas
      if (isNaN(fechaIni.getTime())) return;
      
      // Si hay fecha de fin válida y diferente de inicio, crear dos eventos separados
      if (fechaFin && !isNaN(fechaFin.getTime()) && fechaFin.getTime() > fechaIni.getTime()) {
        // Evento de inicio del curso
        eventos.push({
          title: `Inicio: ${titulo}`,
          start: fechaIni,
          id: `${curso.id}-inicio`,
          extendedProps: {
            cursoId: curso.id,
            esInicio: true,
          },
        });
        
        // Evento de fin del curso
        eventos.push({
          title: `Fin: ${titulo}`,
          start: fechaFin,
          id: `${curso.id}-fin`,
          extendedProps: {
            cursoId: curso.id,
            esFin: true,
          },
        });
      } else {
        // Si no hay fecha de fin válida o es el mismo día, mostrar solo un evento
        eventos.push({
          title: titulo,
          start: fechaIni,
          end: fechaFin && !isNaN(fechaFin.getTime()) ? fechaFin : null,
          id: curso.id,
        });
      }
    });

    setEventos(eventos);
  }, [cursos]);

  return (
    <>
      {/* Contenedor del selector de año */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "10px",
          backgroundColor: "#f5f5f5",
          borderRadius: "5px",
        }}
      >
        <label htmlFor="year-selector" style={{ fontWeight: "bold" }}>
          Año:
        </label>
        <select
          id="year-selector"
          value={selectedYear}
          onChange={handleYearChange}
          style={{
            padding: "5px 10px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontSize: "14px",
            minWidth: "80px",
          }}
        >
          {generateYearOptions().map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
        <span
          style={{
            marginLeft: "10px",
            fontSize: "12px",
            color: "#666",
          }}
        >
          Selecciona un año para navegar rápidamente
        </span>
      </div>

      <FullCalendar
        ref={calendarRef}
        plugins={[
          timeGridPlugin,
          interactionPlugin,
          dayGridPlugin,
          multiMonthPlugin,
        ]}
        initialView="dayGridMonth"
        headerToolbar={{
          left: "prev,next today",
          center: "title",
          right: "multiMonthYear,dayGridMonth,timeGridWeek,timeGridDay",
        }}
        events={eventos}
        editable={true}
        selectable={true}
        eventClick={handleClickChange}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        select={handleDateSelect}
        datesSet={handleDatesSet}
        // Configuraciones adicionales para mejorar la experiencia
        dragRevertDuration={200}
        eventResizableFromStart={false}
        eventDurationEditable={false}
        // Mostrar indicador de carga mientras se actualiza
        loading={loading}
        // Configuraciones específicas para la vista de año
        multiMonthMaxColumns={3} // 3 columnas de meses
        height="auto" // Ajustar altura automáticamente
      />
      <CourseInfoDialog
        curso={curso}
        open={dialogOpen}
        setOpen={setDialogOpen}
      />
    </>
  );
}
